// src/pages/Courses.js
//
// REESCRITO — ya no lee de ../data/courses (estático). Ahora consume
// cursoService.getAll() del backend real, con paginación y filtros por
// categoría (catalogoService.getCategorias()). Usa el CourseCard
// compartido (components/courses/CourseCard.js), ya no define uno
// propio inline.
//
// El estado de inscripción ("Inscrito") se resuelve una sola vez
// contra meService.getMisCursos() y se pasa como prop — evita que
// cada card haga su propia llamada.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Skeleton,
  Pagination,
} from '@mui/material';
import { Search, Clear } from '@mui/icons-material';
import { cursoService, catalogoService, meService } from '../services/api';
import CourseCard from '../components/courses/CourseCard';

function CoursesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [page, setPage] = useState(1);

  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [enrolledIds, setEnrolledIds] = useState(new Set());

  useEffect(() => {
    catalogoService.getCategorias().then(setCategories);
    meService.getMisCursos()
      .then((list) => setEnrolledIds(new Set(list.map((c) => c.id))))
      .catch(() => {});
  }, []);

  const loadCourses = useCallback(async () => {
    setLoading(true);
    const result = await cursoService.getAll({
      categoryId: selectedCategory || undefined,
      page,
      limit: 12,
    });
    setCourses(result.data);
    setTotal(result.total);
    setTotalPages(result.totalPages || 1);
    setLoading(false);
  }, [selectedCategory, page]);

  useEffect(() => { loadCourses(); }, [loadCourses]);

  // Búsqueda por texto — filtrado en cliente sobre la página actual
  // (el backend aún no expone búsqueda de texto libre en /cursos).
  const filteredCourses = useMemo(() => {
    if (!searchQuery) return courses;
    const q = searchQuery.toLowerCase();
    return courses.filter((c) => c.title.toLowerCase().includes(q));
  }, [courses, searchQuery]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setPage(1);
    setSearchParams({});
  };

  const handleCategoryClick = (catId) => {
    setSelectedCategory(catId === selectedCategory ? '' : catId);
    setPage(1);
  };

  return (
    <Box sx={{ minHeight: '100vh', pt: 4 }}>
      <Box sx={{ py: 6, bgcolor: 'rgba(255,255,255,0.02)', borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight={700} gutterBottom>Explora nuestros cursos</Typography>
          <Typography color="text.secondary" variant="h6">
            {loading ? 'Cargando…' : `${total} cursos disponibles`}
          </Typography>
        </Container>
      </Box>

      <Box sx={{ py: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <TextField
              placeholder="Buscar cursos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ minWidth: 280 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search color="disabled" /></InputAdornment> }}
            />
            {(searchQuery || selectedCategory) && (
              <Button startIcon={<Clear />} onClick={clearFilters} size="small">Limpiar</Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label="Todos"
              onClick={() => handleCategoryClick('')}
              color={!selectedCategory ? 'primary' : 'default'}
              variant={!selectedCategory ? 'filled' : 'outlined'}
            />
            {categories.map((cat) => (
              <Chip
                key={cat.id}
                label={cat.name}
                onClick={() => handleCategoryClick(cat.id)}
                sx={{
                  bgcolor: selectedCategory === cat.id ? cat.color : 'transparent',
                  borderColor: cat.color,
                  color: selectedCategory === cat.id ? 'white' : cat.color,
                }}
                variant={selectedCategory === cat.id ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rounded" height={340} />
              </Grid>
            ))}
          </Grid>
        ) : filteredCourses.length > 0 ? (
          <>
            <Grid container spacing={3}>
              {filteredCourses.map((course) => (
                <Grid item xs={12} sm={6} md={4} key={course.id}>
                  <CourseCard course={course} isEnrolled={enrolledIds.has(course.id)} />
                </Grid>
              ))}
            </Grid>
            {totalPages > 1 && !searchQuery && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, v) => setPage(v)}
                  color="primary"
                />
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Search sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h5" gutterBottom>No se encontraron cursos</Typography>
            <Button variant="contained" onClick={clearFilters}>Ver todos los cursos</Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default CoursesPage;
